import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Section from './Section';

const SHADOW = '4px 4px 0px #1e1e1e';

const STORY_TEMPLATES = [
  { emoji: '📰', label: 'News Story', color: '#4A90D9', description: 'Report on something that happened' },
  { emoji: '✨', label: 'Feature Story', color: '#9B59B6', description: 'In-depth story about a topic' },
  { emoji: '🎤', label: 'Profile / Interview', color: '#1ABC9C', description: 'Interview someone and write about them' },
  { emoji: '📚', label: 'Book Review', color: '#9B59B6', description: 'Share your opinion about a book' },
  { emoji: '🎬', label: 'Movie Review', color: '#E74C3C', description: 'Review a movie you watched' },
  { emoji: '🎮', label: 'Video Game Review', color: '#3949ab', description: 'Review a video game' },
  { emoji: '🍕', label: 'Restaurant Review', color: '#E67E22', description: 'Tell us about a place you ate' },
  { emoji: '🧑\u200D🍳', label: 'Recipe', color: '#E67E22', description: 'Share how to make your favorite food' },
  { emoji: '💬', label: 'Opinion Piece', color: '#27AE60', description: 'Write about something you believe' },
  { emoji: '📷', label: 'Photo Essay', color: '#7F8C8D', description: 'Tell a story with pictures and words' },
];

export default function NewspaperShowcase() {
  return (
    <Section background="#FFFFFF" id="newspapers">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
        <Typography
          sx={{
            display: 'inline-block',
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#2C2C2C',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            backgroundColor: '#FFE082',
            border: '2px solid #1e1e1e',
            boxShadow: '2px 2px 0px #1e1e1e',
            px: 2.5,
            py: 0.75,
            borderRadius: '4px',
            mb: 3,
          }}
        >
          New: Classroom Newspapers
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            mb: 2,
          }}
        >
          Every Student is a Journalist
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: '#555',
            maxWidth: 650,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          Teachers create classroom newspapers, and students choose their own story type.
          From news articles to book reviews, every voice gets published.
        </Typography>
      </Box>

      {/* Flow diagram */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr auto 1fr' },
          gap: { xs: 2, md: 0 },
          alignItems: 'center',
          mb: { xs: 5, md: 8 },
          maxWidth: 900,
          mx: 'auto',
        }}
      >
        {[
          { step: '1', title: 'Create a Newspaper', desc: 'Teacher sets up the paper and links classrooms' },
          null,
          { step: '2', title: 'Open an Edition', desc: 'Set the theme, use AI to suggest story assignments' },
          null,
          { step: '3', title: 'Students Write & Publish', desc: 'Students pick stories, write with scaffolds, and turn in' },
        ].map((item, i) => {
          if (!item) {
            return (
              <Typography
                key={i}
                sx={{
                  textAlign: 'center',
                  color: '#CCC',
                  fontSize: '1.5rem',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                &rarr;
              </Typography>
            );
          }
          return (
            <Box
              key={item.step}
              sx={{
                textAlign: 'center',
                p: 3,
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                border: '2px solid #1e1e1e',
                boxShadow: SHADOW,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#2C2C2C',
                  color: '#FFFFFF',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  mb: 1.5,
                }}
              >
                {item.step}
              </Box>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#2C2C2C', mb: 0.5 }}>
                {item.title}
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666', lineHeight: 1.5 }}>
                {item.desc}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Story template grid */}
      <Box>
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.1rem',
            color: '#2C2C2C',
            mb: 3,
          }}
        >
          10 Story Templates for Every Young Writer
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
            },
            gap: 2,
          }}
        >
          {STORY_TEMPLATES.map((t) => (
            <Box
              key={t.label}
              sx={{
                p: 2,
                borderRadius: '6px',
                border: '2px solid #1e1e1e',
                boxShadow: '3px 3px 0px #1e1e1e',
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
                transition: 'all 100ms ease-in-out',
                cursor: 'default',
                '&:hover': {
                  boxShadow: '1px 1px 0px #1e1e1e',
                  transform: 'translate(2px, 2px)',
                },
              }}
            >
              <Typography sx={{ fontSize: '1.8rem', mb: 0.5 }}>{t.emoji}</Typography>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', color: '#2C2C2C', mb: 0.25 }}>
                {t.label}
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', color: '#888', lineHeight: 1.4 }}>
                {t.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}
